import React from 'react';
import { Play, Zap } from 'lucide-react';

// Marketers Orb — red square, 6px radius, bold white megaphone icon
function MarketersOrb({ size = 40 }) {
  return (
    <div
      className="inline-flex items-center justify-center flex-shrink-0"
      style={{
        width: size,
        height: size,
        background: '#e60000',
        borderRadius: '6px',
      }}
      aria-label="Marketers"
    >
      <svg
        width={size * 0.58}
        height={size * 0.58}
        viewBox="0 0 20 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Mouthpiece box */}
        <rect x="0" y="4" width="4" height="8" rx="1" fill="white" />
        {/* Horn — cone flaring right */}
        <path d="M4 3L19 0V16L4 13V3Z" fill="white" />
      </svg>
    </div>
  );
}

// Agent badge chip (pill-md 32px)
function AgentChip({ label }) {
  return (
    <span
      className="inline-flex items-center px-3 py-1 text-xs font-bold text-ink tracking-wide"
      style={{
        background: '#f2f2f2',
        borderRadius: '32px',
        fontSize: '12px',
        fontWeight: 600,
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </span>
  );
}

export default function LandingScreen({ brief, setBrief, onRunGTM, onInstantDemo }) {
  return (
    <div className="min-h-screen bg-canvas font-sans">

      {/* ── Nav bar (ink background) ─────────────────────────────── */}
      <nav
        className="flex items-center justify-between px-8 h-16"
        style={{ background: '#25282b' }}
      >
        <div className="flex items-center gap-3">
          <MarketersOrb size={32} />
          <span className="text-white font-bold text-lg tracking-tight">Marketers</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-white/60 text-sm hidden md:block">For India's B2B founders</span>
          <span
            className="text-white/80 text-xs font-semibold px-3 py-1 tracking-widest"
            style={{ background: 'rgba(255,255,255,0.08)', borderRadius: '32px' }}
          >
            ALPHA
          </span>
        </div>
      </nav>

      {/* ── Hero band (ink background, massive uppercase headline) ── */}
      <div
        className="px-8 pt-20 pb-16"
        style={{ background: '#25282b' }}
      >
        <div className="max-w-5xl mx-auto">
          {/* Eyebrow */}
          <p
            className="text-white/50 mb-6"
            style={{ fontSize: '12px', fontWeight: 800, letterSpacing: '2px', textTransform: 'uppercase' }}
          >
            AI-Powered GTM
          </p>

          {/* Display hero headline — Inter 800 UPPERCASE */}
          <h1
            className="text-white mb-0 uppercase"
            style={{
              fontSize: 'clamp(52px, 8.5vw, 112px)',
              fontWeight: 800,
              lineHeight: 0.95,
              letterSpacing: '-1px',
            }}
            data-testid="hero-headline"
          >
            You describe
            <br />
            the deal.
          </h1>

          {/* Sub-headline — Inter 300 (lighter voice) */}
          <p
            className="text-white/70 mt-5"
            style={{
              fontSize: 'clamp(28px, 4vw, 52px)',
              fontWeight: 300,
              lineHeight: 1.1,
              letterSpacing: '-0.5px',
            }}
          >
            Agents run it.
          </p>

          {/* Agent chips */}
          <div className="flex flex-wrap gap-2 mt-10">
            <AgentChip label="Scout" />
            <AgentChip label="Outreach" />
            <AgentChip label="Objections" />
          </div>
        </div>
      </div>

      {/* ── Content band (white) — brief input + CTAs ───────────── */}
      <div className="bg-canvas px-8 py-16">
        <div className="max-w-2xl mx-auto">
          <p
            className="text-ink mb-3"
            style={{ fontSize: '12px', fontWeight: 800, letterSpacing: '1.5px', textTransform: 'uppercase' }}
          >
            Your GTM Brief
          </p>

          <textarea
            id="gtm-brief"
            data-testid="gtm-brief-input"
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            rows={4}
            className="brief-textarea w-full bg-canvas text-ink text-base p-4 resize-none leading-relaxed"
            style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}
            spellCheck={false}
          />

          {/* CTAs — large pill buttons */}
          <div className="flex flex-wrap gap-3 mt-6">
            {/* Primary — red fill pill */}
            <button
              data-testid="run-gtm-button"
              onClick={onRunGTM}
              className="inline-flex items-center gap-2 font-normal text-white transition-all duration-150"
              style={{
                background: '#e60000',
                border: '1px solid #e60000',
                borderRadius: '60px',
                padding: '12px 28px',
                fontSize: '18px',
                lineHeight: '28px',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#cc0000')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '#e60000')}
            >
              <Play size={16} strokeWidth={2.5} />
              Run GTM
            </button>

            {/* Outline red pill */}
            <button
              data-testid="instant-demo-button"
              onClick={onInstantDemo}
              className="inline-flex items-center gap-2 font-normal transition-all duration-150"
              style={{
                background: '#ffffff',
                border: '1px solid #e60000',
                borderRadius: '60px',
                padding: '12px 28px',
                fontSize: '18px',
                lineHeight: '28px',
                color: '#e60000',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#fff5f5')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '#ffffff')}
            >
              <Zap size={16} strokeWidth={2} />
              Instant Demo
            </button>
          </div>

          <p className="text-body-text text-sm mt-4">
            Instant Demo runs in 1.5 seconds — no API call, hardcoded results. Run GTM calls three live AI agents in parallel.
          </p>
        </div>
      </div>

      {/* ── Feature card grid ────────────────────────────────────── */}
      <div
        className="px-8 py-16"
        style={{ background: '#f2f2f2', borderTop: '1px solid #e8e8e8' }}
      >
        <div className="max-w-5xl mx-auto">
          <p
            className="text-ink mb-2"
            style={{ fontSize: '12px', fontWeight: 800, letterSpacing: '1.5px', textTransform: 'uppercase' }}
          >
            How it works
          </p>
          <h2
            className="text-ink mb-10"
            style={{ fontSize: '32px', fontWeight: 700, lineHeight: '40px' }}
          >
            Three agents. One brief.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <AgentCard
              number="01"
              title="Scout"
              body="Scans the Indian B2B market and surfaces 3 high-fit target companies with a fit score and one sharp, actionable insight each."
              accentLabel="Targets"
            />
            <AgentCard
              number="02"
              title="Outreach"
              body="Writes a cold email your VP Sales would actually send — rupee-anchored, peer-to-peer tone, no corporate speak."
              accentLabel="Email"
            />
            <AgentCard
              number="03"
              title="Objections"
              body="Pre-loads the 3 hardest objections Indian B2B buyers raise — with a battle-tested two-sentence response to each."
              accentLabel="Battle Card"
            />
          </div>
        </div>
      </div>

    </div>
  );
}

function AgentCard({ number, title, body, accentLabel }) {
  return (
    <div
      className="bg-canvas p-6 flex flex-col"
      style={{ borderRadius: '6px', border: '1px solid #e8e8e8' }}
    >
      <div className="flex items-center justify-between mb-4">
        <span
          className="text-primary font-bold"
          style={{ fontSize: '12px', letterSpacing: '1px', textTransform: 'uppercase' }}
        >
          {number}
        </span>
        <span
          className="text-xs font-semibold text-body-text px-2 py-0.5"
          style={{ background: '#f2f2f2', borderRadius: '32px', letterSpacing: '0.5px', textTransform: 'uppercase' }}
        >
          {accentLabel}
        </span>
      </div>
      <h3 className="text-ink font-bold text-xl mb-3" style={{ lineHeight: '1.2' }}>{title}</h3>
      <p className="text-body-text text-sm leading-relaxed flex-1">{body}</p>
    </div>
  );
}
